import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StudyShare } from '../../../services/study-share.service';

export interface PatientShareHistoryModalData {
  facilityShares: { facilityId: string; facilityName: string; latestShare: StudyShare | null }[];
}

@Component({
  selector: 'app-study-share-patient-history-modal',
  templateUrl: './study-share-patient-history-modal.component.html',
  styleUrls: ['./study-share-patient-history-modal.component.scss'],
  standalone: false,
})
export class StudySharePatientHistoryModalComponent {
  displayedColumns = ['facility', 'lastShared', 'status', 'expiry'];

  constructor(@Inject(MAT_DIALOG_DATA) public data: PatientShareHistoryModalData) {}

  statusLabel(share: StudyShare): string {
    const map: Record<string, string> = {
      active: 'Active',
      expiring_soon: 'Expiring Soon',
      expired: 'Expired',
      deleted: 'Deleted',
      deletion_failed: 'Deletion Failed',
      re_requested: 'Re-share Requested',
      reshared: 'Approved',
      rejected: 'Rejected',
      uploaded_to_pacs: 'Uploaded to PACS',
    };
    return map[share.status] || share.status;
  }

  statusClass(share: StudyShare): string {
    const map: Record<string, string> = {
      active: 'status--active',
      expiring_soon: 'status--expiring',
      expired: 'status--expired',
      deleted: 'status--deleted',
      deletion_failed: 'status--failed',
      re_requested: 'status--pending',
      reshared: 'status--approved',
      rejected: 'status--rejected',
      uploaded_to_pacs: 'status--pacs',
    };
    return map[share.status] || '';
  }

  formatDate(ts: { toDate(): Date } | undefined): string {
    if (!ts) return '—';
    return ts.toDate().toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }

  expiryText(share: StudyShare): string {
    if (!share.effective_expires_at) return '—';
    const d = share.effective_expires_at.toDate();
    const now = Date.now();
    if (d.getTime() <= now) return `Expired ${this.formatDate(share.effective_expires_at)}`;
    return `Until ${this.formatDate(share.effective_expires_at)}`;
  }

  get sortedRows() {
    const order = (s: StudyShare | null): number => {
      if (!s) return 3;
      if (s.status === 'active' || s.status === 'expiring_soon') return 0;
      if (s.status === 're_requested') return 1;
      return 2;
    };
    return [...this.data.facilityShares].sort((a, b) => {
      const diff = order(a.latestShare) - order(b.latestShare);
      if (diff !== 0) return diff;
      const aTime = a.latestShare?.shared_at?.toDate().getTime() ?? 0;
      const bTime = b.latestShare?.shared_at?.toDate().getTime() ?? 0;
      return bTime - aTime;
    });
  }
}
